import React, { useEffect, useRef, useState } from "react";

/**
 * "Scroll to read", set in the left margin (Sam, 2026-09-11) -- a nudge that the
 * index is only the top of the page.
 *
 * It used to fade over the first 450px of scroll, which put it out of sight
 * while the reader was still in the INDEX -- exactly where the nudge is for.
 * It now holds until the long-form reading actually begins and dissolves as
 * that arrives (Sam, 2026-09-17: "should only dissolve/fade once you get into
 * the long form story entry portion").
 *
 * This page scrolls inside a div, not the window (useScrollFade.js, v3: a
 * listener bound to `window` never fired here), so the cue finds the nearest
 * scrolling ancestor and listens to THAT.
 *
 * Shown only where there is a margin to sit in: the sidebar is 300px, so on
 * narrower screens the cue would land on top of the content. No nudge
 * animation for readers who ask for reduced motion.
 */
// How far ahead of the reading section the cue starts dissolving.
const FADE_DISTANCE = 450;

function scrollingAncestor(el) {
  for (let n = el && el.parentElement; n; n = n.parentElement) {
    const oy = getComputedStyle(n).overflowY;
    if (oy === "auto" || oy === "scroll") return n;
  }
  return null;
}

export default function ScrollCue() {
  const ref = useRef(null);
  const [opacity, setOpacity] = useState(1);

  useEffect(() => {
    const scroller = scrollingAncestor(ref.current);
    if (!scroller) return undefined;
    let frame = 0;
    const measure = () => {
      // Where the long-form reading starts. Until it is within FADE_DISTANCE of
      // the top of the panel the cue stays fully visible, however far the reader
      // has scrolled through the index.
      const reading = document.querySelector("[data-footprint-reading]");
      if (!reading) {
        // No reading section on this page (an index of one, say) -- fall back to
        // the original behaviour rather than leaving the cue up for ever.
        return Math.max(0, 1 - scroller.scrollTop / FADE_DISTANCE);
      }
      const ahead = reading.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
      if (ahead >= FADE_DISTANCE) return 1;
      return Math.max(0, ahead / FADE_DISTANCE);
    };
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setOpacity(measure()));
    };
    onScroll();
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      scroller.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none absolute inset-y-0 left-[22px] hidden min-[1360px]:block"
    >
      <style>{`@keyframes fpScrollNudge{0%,100%{transform:translateY(0)}50%{transform:translateY(5px)}}
@media (prefers-reduced-motion: reduce){.fp-scroll-nudge{animation:none !important}}`}</style>
      <div
        className="sticky top-[42vh] flex flex-col items-center gap-[10px] transition-opacity duration-300"
        style={{ opacity }}
      >
        <span className="font-blairBody font-semibold text-[12px] tracking-[0.3em] uppercase
                         text-blairGoldDk dark:text-blairGold [writing-mode:vertical-rl] rotate-180">
          Scroll to read
        </span>
        <span className="w-px h-[70px] bg-gradient-to-b from-blairGoldDk to-transparent dark:from-blairGold" />
        <span
          className="fp-scroll-nudge text-[13px] text-blairGoldDk dark:text-blairGold"
          style={{ animation: "fpScrollNudge 2.2s ease-in-out infinite" }}
        >
          ↓
        </span>
      </div>
    </div>
  );
}
